/**
 * @description
 * User management modal component for adding and editing client users.
 * Uses Radix UI Dialog pattern with proper form validation and error handling.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 6, 2025
 */

import { useState, useEffect } from 'react';
import { X, Save, UserPlus, User } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { ClientUser } from '../../services/api';

interface UserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  user?: ClientUser | null;
  onUserAdded: (user: ClientUser) => void;
  onUserUpdated: (user: ClientUser) => void;
}

interface UserFormData {
  userEmail: string;
  userName: string;
  role: 'developer' | 'manager' | 'owner';
  syncWithPlatform: boolean;
}

/**
 * User management modal component
 */
export function UserManagementModal({ 
  isOpen, 
  onClose, 
  clientId, 
  user, 
  onUserAdded, 
  onUserUpdated 
}: UserManagementModalProps) {
  const [formData, setFormData] = useState<UserFormData>({
    userEmail: '',
    userName: '',
    role: 'developer',
    syncWithPlatform: false,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load user data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (user) {
        setFormData({
          userEmail: user.userEmail,
          userName: user.userName,
          role: user.role,
          syncWithPlatform: false, // Don't pre-fill sync option for edit
        });
      } else {
        setFormData({
          userEmail: '',
          userName: '',
          role: 'developer',
          syncWithPlatform: false,
        });
      }
      setErrors({});
    }
  }, [isOpen, user]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.userEmail.trim()) {
      newErrors.userEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail)) {
      newErrors.userEmail = 'Please enter a valid email address';
    }

    if (!formData.userName.trim()) {
      newErrors.userName = 'Name is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    
    try {
      if (user) {
        // Update existing user
        const response = await api.updateClientUser(clientId, user.id, {
          role: formData.role,
        });
        onUserUpdated(response.user);
        toast.success('User updated successfully');
      } else {
        // Add new user
        const response = await api.addClientUser(clientId, {
          userEmail: formData.userEmail,
          userName: formData.userName,
          role: formData.role,
          syncWithPlatform: formData.syncWithPlatform,
        });
        onUserAdded(response.user);
        toast.success('User added successfully');
      }
      onClose();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(`Failed to save user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof UserFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-md rounded-lg border bg-card p-6 shadow-lg">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <User className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold text-card-foreground">Edit User</h2>
              </>
            ) : (
              <>
                <UserPlus className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold text-card-foreground">Add New User</h2>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            disabled={saving}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field */}
          <div>
            <label htmlFor="userEmail" className="block text-sm font-medium text-card-foreground mb-1">
              Email Address *
            </label>
            <input
              type="email"
              id="userEmail"
              value={formData.userEmail}
              onChange={(e) => handleInputChange('userEmail', e.target.value)}
              disabled={!!user || saving}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground ${
                errors.userEmail ? 'border-destructive' : 'border-border'
              } ${!!user || saving ? 'bg-muted cursor-not-allowed' : ''}`}
              placeholder="user@example.com"
            />
            {errors.userEmail && (
              <p className="mt-1 text-sm text-destructive">{errors.userEmail}</p>
            )}
          </div>

          {/* Name Field */}
          <div>
            <label htmlFor="userName" className="block text-sm font-medium text-card-foreground mb-1">
              Full Name *
            </label>
            <input
              type="text"
              id="userName"
              value={formData.userName}
              onChange={(e) => handleInputChange('userName', e.target.value)}
              disabled={saving}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground ${
                errors.userName ? 'border-destructive' : 'border-border'
              }`}
              placeholder="John Doe"
            />
            {errors.userName && (
              <p className="mt-1 text-sm text-destructive">{errors.userName}</p>
            )}
          </div>

          {/* Role Field */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-card-foreground mb-1">
              Role *
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value as 'developer' | 'manager' | 'owner')}
              disabled={saving}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground ${
                errors.role ? 'border-destructive' : 'border-border'
              }`}
            >
              <option value="developer">Developer</option>
              <option value="manager">Manager</option>
              <option value="owner">Owner</option>
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-destructive">{errors.role}</p>
            )}
          </div>

          {/* Sync with Platform (only for new users) */}
          {!user && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="syncWithPlatform"
                checked={formData.syncWithPlatform}
                onChange={(e) => handleInputChange('syncWithPlatform', e.target.checked)}
                disabled={saving}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring focus:ring-2"
              />
              <label htmlFor="syncWithPlatform" className="text-sm text-card-foreground">
                Sync with platform.byteable.ai
              </label>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>{user ? 'Update User' : 'Add User'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
